/**
 * Emit hosts for the `template` analytics group — 13 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import {
  type TemplateAuthorGroup,
  type TemplateVisibility,
} from '@heidi/schema/ml-scribe';

export function templateCreateAskAi(payload: { aiCommandText: string }) {
  // Fires when AI content finishes streaming — intent signal only; the
  // authoritative `template_created` fires at save time.
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateCreatedUseAskAi,
      aiCommandText: payload.aiCommandText,
    },
  });
}

export function trackPDFTemplateCreated({
  templateId,
  templateName,
  teamId,
  sectionCount,
}: {
  templateId: string;
  templateName: string;
  teamId?: string | null;
  sectionCount?: number;
}) {
  track({
    event_name: TrackingEventName.TemplateCreated,
    props: {
      template_id: templateId,
      template_name: templateName,
      team_id: teamId ?? undefined,
      src: 'pdf',
      section_count: sectionCount,
    },
  });
}

export function trackTemplateDeleted(templateId: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateDeleted,
      templateId,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateDeleted,
    props: {
      template_id: templateId,
      delete_action: 'template_deleted',
    },
  });
}

export function trackTemplateEdited({
  templateId,
  templateVisibility,
}: {
  templateId: string;
  templateVisibility: TemplateVisibility;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateEdited,
      templateId,
      templateVisibility,
    },
  });
}

export function trackTemplateEditorUpdated({
  templateId,
  tabType,
  version,
}: {
  templateId?: string;
  tabType: 'example' | 'structure';
  version: 'v1' | 'v2';
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateEditorUpdated,
    props: {
      template_id: templateId,
      tab_type: tabType,
      template_editor_version: version,
    },
  });
}

export function trackTemplateFavourited({
  templateId,
  templateType,
  templateFavourited,
}: {
  templateId: string;
  templateType: TemplateVisibility;
  templateFavourited: boolean;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateFavourited,
      templateId,
      templateType,
      templateFavourited,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateUpdated,
    props: {
      template_id: templateId,
      update_action: 'favourited',
      template_type: templateType,
      is_favorite: templateFavourited,
    },
  });
}

export function trackTemplateGeneration({
  templateId,
  src,
  useSections,
}: {
  templateId?: string;
  src: 'chat' | 'example' | 'structure';
  useSections?: boolean;
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateGeneration,
    props: {
      template_id: templateId,
      src,
      use_sections: useSections,
    },
  });
}

export function trackTemplateLibraryFilteredApplied(filter: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateLibraryFilteredApplied,
      filter,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateSearched,
    props: {
      query: filter,
      search_action: 'filter_applied',
    },
  });
}

export function trackTemplateLibrarySearched(searchText: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateLibrarySearched,
      searchQuery: searchText,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateSearched,
    props: {
      query: searchText,
      search_action: 'searched',
    },
  });
}

export function trackTemplateLibrarySortApplied(sort: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateLibrarySortApplied,
      sort,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateSearched,
    props: {
      query: sort,
      search_action: 'sort_applied',
    },
  });
}

export function trackTemplateLibraryViewed() {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateLibraryViewed,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateViewed,
    props: {},
  });
}

export function trackTemplateOpened({
  templateId,
  templateAuthorGroup,
  version,
}: {
  templateId: string;
  templateAuthorGroup?: TemplateAuthorGroup | null;
  version: 'v1' | 'v2';
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateOpened,
    props: {
      template_id: templateId,
      template_author_group: templateAuthorGroup,
      template_editor_version: version,
    },
  });
}

export function trackTemplateUpdated({
  templateId,
  templateName,
  teamId,
  version,
  sectionCount,
  templateType,
  useSections,
}: {
  templateId?: string;
  templateName?: string;
  teamId?: string;
  version: 'v1' | 'v2';
  sectionCount?: number;
  /**
   * Template category of the saved template. Without it a patient-summary
   * template edit is indistinguishable from any other save, since
   * `template_name` is clinician-authored.
   */
  templateType?: string;
  useSections?: boolean;
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateUpdated,
    props: {
      template_id: templateId,
      template_name: templateName,
      team_id: teamId,
      template_editor_version: version,
      section_count: sectionCount,
      template_type: templateType,
      use_sections: useSections,
    },
  });
}
